(function () {
  'use strict';

  const promo = window.theme && window.theme.promo;

  if (!promo || !promo.key) {
    return;
  }

  const storedKey = sessionStorage.getItem('youngle_promo');

  if (!promo.sitewide && storedKey !== promo.key) {
    return;
  }

  document.documentElement.dataset.springSale = 'active';

  const SUBSCRIPTION_DISCOUNT_PERCENT = 15;

  /**
   * Compute the discounted price in cents, matching Shopify's rounding:
   * floor the discount amount, then subtract from the original. This
   * avoids the 1-cent mismatches that occur when rounding the final
   * price directly (Math.round(price * multiplier)).
   */
  function applyPercentDiscount(cents, percent) {
    return cents - Math.floor(cents * percent / 100);
  }

  const promoStrings = promo.strings || {};

  /**
   * Replace {{ percent }} placeholders in a promo locale string.
   */
  function formatPromoString(template, percent) {
    if (!template) {
      return '';
    }

    return template.replace(/\{\{\s*percent\s*\}\}/g, String(percent));
  }

  const moneyFormat = theme.settings.currency_code_enable
    ? theme.moneyWithCurrencyFormat
    : theme.moneyFormat;

  /* ------------------------------------------------------------------ */
  /*  Shared helpers                                                     */
  /* ------------------------------------------------------------------ */

  /**
   * Format a price in cents using the theme's currency formatter,
   * respecting the active currency and the currency-code-display setting.
   * Falls back to Intl.NumberFormat with the store's currency ISO code,
   * then to a plain decimal string as a last resort.
   */
  function formatMoney(cents) {
    if (typeof themeCurrency !== 'undefined' && themeCurrency.formatMoney) {
      return themeCurrency.formatMoney(cents, moneyFormat);
    }

    try {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: theme.current_iso_code || 'EUR'
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
  }

  /**
   * Parse a formatted price string (e.g. "19,90 €", "€19.90", "1.234,56 €")
   * into an integer number of cents. Handles both EU (comma-decimal) and
   * US/UK (period-decimal) formats by inspecting which separator comes last.
   * Returns 0 when the input cannot be parsed.
   */
  function parsePriceToCents(text) {
    const cleaned = text.replace(/[^\d.,]/g, '');

    if (!cleaned) {
      return 0;
    }

    const lastCommaIndex = cleaned.lastIndexOf(',');
    const lastPeriodIndex = cleaned.lastIndexOf('.');
    let normalized;

    if (lastCommaIndex > lastPeriodIndex) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (lastPeriodIndex > lastCommaIndex) {
      normalized = cleaned.replace(/,/g, '');
    } else if (lastCommaIndex >= 0) {
      const digitsAfterComma = cleaned.substring(lastCommaIndex + 1);
      normalized = digitsAfterComma.length <= 2
        ? cleaned.replace(',', '.')
        : cleaned.replace(/,/g, '');
    } else {
      normalized = cleaned;
    }

    const value = parseFloat(normalized);

    return isNaN(value) ? 0 : Math.round(value * 100);
  }

  /* ------------------------------------------------------------------ */
  /*  Product grid cards (collection pages, LP sections)                 */
  /* ------------------------------------------------------------------ */

  /**
   * Transform a single grid-card price element: replace its content with
   * the discounted price and append the original as a strikethrough.
   * Hides any existing compare-at element to avoid double strikethrough
   * on products that already had a sale price.
   */
  function processGridPrice(priceElement) {
    if (priceElement.dataset.promoApplied) {
      return;
    }

    const originalCents = parseInt(priceElement.dataset.priceCents, 10);

    if (isNaN(originalCents) || originalCents <= 0) {
      return;
    }

    const discountedCents = applyPercentDiscount(originalCents, promo.discountPercent);
    const originalFormatted = priceElement.textContent.trim();

    priceElement.innerHTML = formatMoney(discountedCents);
    priceElement.dataset.promoApplied = '1';

    const strikeElement = document.createElement('s');
    strikeElement.setAttribute('data-promo-original', '');
    strikeElement.textContent = originalFormatted;
    priceElement.insertAdjacentElement('afterend', strikeElement);

    const existingCompare = priceElement.parentNode.querySelector('[data-promo-compare]');

    if (existingCompare) {
      existingCompare.style.display = 'none';
    }
  }

  /**
   * Find and process all unprocessed grid-card price elements within a root.
   */
  function processAllGridPrices(root) {
    const priceElements = (root || document).querySelectorAll(
      '[data-promo-price]:not([data-promo-applied])'
    );

    for (const priceElement of priceElements) {
      processGridPrice(priceElement);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  PDP main price                                                     */
  /* ------------------------------------------------------------------ */

  /**
   * Apply the promo discount to the PDP's main price display.
   * Uses the existing [data-compare-price] element for the strikethrough.
   */
  function processPdpPrice() {
    const priceElement = document.querySelector('[data-product-price]');

    if (!priceElement || priceElement.dataset.promoApplied) {
      return;
    }

    const originalCents = parsePriceToCents(priceElement.textContent);

    if (originalCents <= 0) {
      return;
    }

    const discountedCents = applyPercentDiscount(originalCents, promo.discountPercent);
    const originalFormatted = priceElement.textContent.trim();

    priceElement.textContent = formatMoney(discountedCents);
    priceElement.dataset.promoApplied = '1';
    priceElement.dataset.promoOriginalCents = String(originalCents);

    const compareElement = priceElement
      .closest('[data-price-wrapper]')
      ?.querySelector('[data-compare-price]');

    if (compareElement) {
      compareElement.textContent = originalFormatted;
      compareElement.classList.remove('hidden');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Appstle subscription widget                                        */
  /* ------------------------------------------------------------------ */

  /**
   * Apply the promo discount to an Appstle price element. Stores the
   * original cents as a data attribute on first processing so that
   * re-processing after a plan toggle can distinguish between "Appstle
   * changed the price" (needs re-discount) and "our discounted text is
   * still there" (skip).
   *
   * Returns the original cents used, or 0 if nothing was processed.
   */
  function discountAppstlePrice(priceElement) {
    if (!priceElement) {
      return 0;
    }

    if (priceElement.dataset.promoApplied) {
      return parseInt(priceElement.dataset.promoOriginalCents, 10) || 0;
    }

    const storedOriginalCents = parseInt(priceElement.dataset.promoOriginalCents, 10);

    if (storedOriginalCents > 0) {
      const currentCents = parsePriceToCents(priceElement.textContent);
      const expectedDiscountedCents = applyPercentDiscount(storedOriginalCents, promo.discountPercent);

      if (currentCents === expectedDiscountedCents) {
        priceElement.dataset.promoApplied = '1';
        return storedOriginalCents;
      }
    }

    const originalCents = parsePriceToCents(priceElement.textContent);

    if (originalCents <= 0) {
      return 0;
    }

    const discountedCents = applyPercentDiscount(originalCents, promo.discountPercent);

    priceElement.textContent = formatMoney(discountedCents);
    priceElement.dataset.promoApplied = '1';
    priceElement.dataset.promoOriginalCents = String(originalCents);

    return originalCents;
  }

  /**
   * Apply the promo discount to the Appstle one-time purchase price.
   * Populates the existing (empty) compare-at element with the original.
   */
  function processAppstleOneTimePrice() {
    const wrapper = document.querySelector('.appstle_one_time_price_wrapper');

    if (!wrapper) {
      return;
    }

    const priceElement = wrapper.querySelector('.appstle_subscription_amount');
    const originalCents = discountAppstlePrice(priceElement);

    if (originalCents > 0) {
      const compareElement = wrapper.querySelector('.appstle_subscription_amount_compare_at_price');

      if (compareElement && !compareElement.textContent.trim()) {
        compareElement.textContent = formatMoney(originalCents);
      }
    }

    const radioLabel = wrapper
      .closest('.appstle_subscription_wrapper_option')
      ?.querySelector('.appstle_radio_label');

    if (radioLabel && !radioLabel.querySelector('.promo-save-badge')) {
      const textElement = radioLabel.querySelector('.appstle_one_time_text');

      if (textElement) {
        const badge = document.createElement('span');
        badge.className = 'promo-save-badge';
        badge.textContent = formatPromoString(promoStrings.saveBadge, promo.discountPercent);
        textElement.insertAdjacentElement('afterend', badge);
      }
    }
  }

  /**
   * Apply the promo discount to the Appstle subscribe-and-save price.
   * The subscription price is already reduced by the subscription discount
   * (e.g. 15%); the sale discount is applied on top of that so subscribers
   * always get the deeper combined saving.
   *
   * Also updates the "SAVE X%" badge to reflect the combined discount.
   */
  function processAppstleSubscriptionPrice() {
    const wrapper = document.querySelector('.appstle_subscription_amount_wrapper');

    if (!wrapper) {
      return;
    }

    const priceElement = wrapper.querySelector('.appstle_subscription_amount');
    discountAppstlePrice(priceElement);

    const optionWrapper = wrapper.closest('.appstle_subscription_wrapper_option');

    if (optionWrapper && !optionWrapper.querySelector('.promo-save-badge')) {
      const textElement = optionWrapper.querySelector('.appstle_subscribe_save_text');

      if (textElement) {
        const badge = document.createElement('span');
        badge.className = 'promo-save-badge';
        badge.textContent = formatPromoString(promoStrings.saveBadge, combinedSubscriptionPercent);
        textElement.insertAdjacentElement('afterend', badge);
      }
    }
  }

  /**
   * Display-only: used in the subscription badge and "today" copy, never in
   * price calculations. `promo.subscriptionDisplayPercent` overrides the
   * computed value to show a rounder number; keep it at or below the real
   * stacked discount so the copy never over-promises.
   */
  const combinedSubscriptionPercent = promo.subscriptionDisplayPercent || Math.floor(
    (1 - (1 - SUBSCRIPTION_DISCOUNT_PERCENT / 100) * (1 - promo.discountPercent / 100)) * 100
  );

  /**
   * Replace the Appstle subscription description with a savings breakdown
   * showing the combined discount (subscription + spring sale) for today's
   * order versus the recurring subscription discount on future deliveries.
   */
  function processAppstleDescription() {
    const descriptions = document.querySelectorAll('.appstle_subscription_description_text');

    for (const description of descriptions) {
      if (description.dataset.promoApplied) {
        continue;
      }

      description.innerHTML =
        formatPromoString(promoStrings.subscriptionTodayHtml, combinedSubscriptionPercent) +
        formatPromoString(promoStrings.subscriptionFuture, SUBSCRIPTION_DISCOUNT_PERCENT);
      description.dataset.promoApplied = '1';
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Cart discount                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Apply the promo discount code to the cart via Shopify's Cart AJAX API.
   * Uses a sessionStorage guard to avoid redundant requests on every page load.
   * Verifies the discount was actually applied by checking the cart response.
   */
  /**
   * Check whether the promo discount code is currently on the cart.
   */
  function isDiscountOnCart(cart) {
    const discountApplications = cart.cart_level_discount_applications || [];

    return discountApplications.some(function (application) {
      return application.title.toUpperCase() === promo.discountCode.toUpperCase();
    });
  }

  function applyDiscountCode() {
    fetch(theme.routes.root + 'cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (isDiscountOnCart(cart)) {
          return;
        }

        return fetch(theme.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: promo.discountCode })
        })
          .then(function () {
            return fetch(theme.routes.root + 'cart.js');
          })
          .then(function (response) {
            return response.json();
          })
          .then(function (updatedCart) {
            if (isDiscountOnCart(updatedCart)) {
              refreshCartDrawer();
            }
          });
      })
      .catch(function () {});
  }

  /**
   * Silently refresh the cart drawer so applied discounts are visible
   * without a page reload. Delegates to the theme's own getCart()
   * which re-fetches the api-cart-items section and rebuilds the
   * drawer DOM without opening it.
   */
  function refreshCartDrawer() {
    if (window.cart && typeof window.cart.getCart === 'function') {
      window.cart.getCart();
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Initialisation and observers                                       */
  /* ------------------------------------------------------------------ */

  /** Process all price surfaces and apply the cart discount. */
  function initialise() {
    processAllGridPrices();
    processPdpPrice();
    processAppstleOneTimePrice();
    processAppstleSubscriptionPrice();
    processAppstleDescription();
    applyDiscountCode();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise);
  } else {
    initialise();
  }

  /**
   * Watch for dynamically injected product cards (e.g. AJAX collection
   * loading, quick view modals) and process their prices as they appear.
   * Also catches the Appstle widget when it renders asynchronously.
   */
  const gridObserver = new MutationObserver(function (mutations) {
    for (const mutation of mutations) {
      for (const addedNode of mutation.addedNodes) {
        if (addedNode.nodeType !== 1) {
          continue;
        }

        if (addedNode.matches('[data-promo-price]:not([data-promo-applied])')) {
          processGridPrice(addedNode);
        }

        processAllGridPrices(addedNode);

        if (
          addedNode.matches('.appstle_sub_widget') ||
          addedNode.querySelector('.appstle_sub_widget')
        ) {
          processAppstleOneTimePrice();
          processAppstleSubscriptionPrice();
          processAppstleDescription();
        }
      }
    }
  });

  gridObserver.observe(document.documentElement, { childList: true, subtree: true });

  /**
   * Re-apply promo prices when the user switches between "Single purchase"
   * and "Subscribe & Save" in the Appstle widget. Appstle updates its price
   * elements in place, overwriting our modifications. We listen for changes
   * on the selling-plan radio inputs and re-process after a short delay to
   * let Appstle finish its DOM updates.
   */
  function reprocessAppstlePrices() {
    const widget = document.querySelector('.appstle_sub_widget');

    if (!widget) {
      return;
    }

    for (const element of widget.querySelectorAll('[data-promo-applied]')) {
      delete element.dataset.promoApplied;
    }

    processAppstleOneTimePrice();
    processAppstleSubscriptionPrice();
    processAppstleDescription();
  }

  document.addEventListener('change', function (event) {
    if (
      event.target.matches('input[name="selling_plan"]') ||
      event.target.matches('input[name="selling_plan_radio"]')
    ) {
      setTimeout(reprocessAppstlePrices, 200);
    }
  });

  document.addEventListener('click', function (event) {
    if (event.target.closest('.appstle_subscription_wrapper_option')) {
      setTimeout(reprocessAppstlePrices, 200);
    }
  });

  /**
   * Watch for Appstle widget re-renders on variant changes.
   * When Appstle replaces its widget HTML (new nodes without our data
   * attributes), the gridObserver catches the insertion and re-processes.
   * For the PDP main price, the theme replaces text content on variant
   * switch, which the gridObserver also catches as childList mutations.
   */
  function observeVariantChanges() {
    const priceWrapper = document.querySelector('[data-price-wrapper]');

    if (!priceWrapper) {
      return;
    }

    let isProcessing = false;

    const pdpObserver = new MutationObserver(function () {
      if (isProcessing) {
        return;
      }

      const priceElement = priceWrapper.querySelector('[data-product-price]');

      if (!priceElement || !priceElement.dataset.promoApplied) {
        return;
      }

      const currentCents = parsePriceToCents(priceElement.textContent);
      const storedOriginal = parseInt(priceElement.dataset.promoOriginalCents, 10);
      const expectedCents = applyPercentDiscount(storedOriginal, promo.discountPercent);

      if (currentCents === expectedCents) {
        return;
      }

      isProcessing = true;
      delete priceElement.dataset.promoApplied;
      delete priceElement.dataset.promoOriginalCents;
      processPdpPrice();

      requestAnimationFrame(function () {
        isProcessing = false;
      });
    });

    pdpObserver.observe(priceWrapper, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(observeVariantChanges, 500);
    });
  } else {
    setTimeout(observeVariantChanges, 500);
  }
})();
